/**
 * KAN-837: часы менеджера в карточке кабинета (владелец правит через
 * PATCH /managers/{id}/ {work_hours}). Черновик открывается кнопкой
 * "Изменить", проверяется так же, как в приложении менеджера, после
 * сохранения обновляются запросы менеджеров (useManagerMutations).
 */
import { useCallback, useState } from 'react';
import { useManagerMutations } from '@/hooks/useManagers';
import { FORM_ERROR_KEY, profileErrorsFrom } from '@/lib/managerProfileErrors';
import {
  draftToWorkHours,
  validateWorkHours,
  workHoursToDraft,
  type WorkHoursDraft,
} from '@/lib/workHours';
import type { WorkHours } from '@/types/manager';

export function useManagerWorkHours(managerId: string, value: WorkHours | undefined) {
  const { update } = useManagerMutations();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<WorkHoursDraft>(() => workHoursToDraft(value));
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(() => {
    setDraft(workHoursToDraft(value));
    setError(null);
    setEditing(true);
  }, [value]);

  const cancel = useCallback(() => {
    setError(null);
    setEditing(false);
  }, []);

  const change = useCallback((next: WorkHoursDraft) => {
    setDraft(next);
    setError(null);
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    const local = validateWorkHours(draft);
    if (local) {
      setError(local);
      return false;
    }
    try {
      await update.mutateAsync({ id: managerId, data: { work_hours: draftToWorkHours(draft) } });
      setEditing(false);
      setError(null);
      return true;
    } catch (err) {
      const errors = profileErrorsFrom(err, ['work_hours']);
      setError(errors.work_hours ?? errors[FORM_ERROR_KEY] ?? null);
      return false;
    }
  }, [draft, managerId, update]);

  return { editing, draft, error, saving: update.isPending, start, cancel, change, save };
}
